import React from 'react';
import { useTheme } from '../../ThemeContext';

/**
 * Reusable List Modal Component
 * 
 * A professional modal component for displaying lists of data with sticky header
 * and scrollable content. Styled consistently with BillingRestrictionPopup.
 * 
 * @param {boolean} open - Controls modal visibility
 * @param {string} title - Modal title text
 * @param {Array} data - Array of items to display in the list
 * @param {Function} onClose - Function to close the modal
 * @param {string} [subtitle] - Optional subtitle text
 * @param {number} [maxWidth=500] - Maximum width of the modal in pixels
 * @param {string} [maxHeight='80vh'] - Maximum height of the modal
 * @param {Function} [renderItem] - Custom render function for list items (item, index) => JSX
 * @param {boolean} [showIndex=false] - Whether to show index numbers
 * @param {Object} [sx] - Additional styling overrides
 * 
 * @example
 * <ListModal
 *   open={modalOpen}
 *   title="Insurance Plans"
 *   subtitle="Facility: Main Hospital"
 *   data={['Plan A', 'Plan B', 'Plan C']}
 *   onClose={() => setModalOpen(false)}
 * />
 * 
 * @example
 * // Custom render function
 * <ListModal
 *   open={modalOpen}
 *   title="User Details"
 *   data={users}
 *   renderItem={(user, index) => (
 *     <div>
 *       <strong>{user.name}</strong>
 *       <div>{user.email}</div>
 *     </div>
 *   )}
 *   onClose={() => setModalOpen(false)}
 * />
 */
const ListModal = ({
  open,
  title,
  data = [],
  onClose,
  subtitle,
  maxWidth = 500,
  maxHeight = '80vh',
  renderItem,
  showIndex = false,
  sx = {}
}) => {
  const { themeMode, currentTheme } = useTheme();

  if (!open) return null;

  // Theme-based styling inspired by BillingRestrictionPopup
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    borderRadius: '16px',
    padding: '0',
    maxWidth: `${maxWidth}px`,
    width: '90%',
    maxHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    ...sx
  };

  const headerStyle = {
    padding: '24px 24px 16px 24px',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    flexShrink: 0
  };

  const titleRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: subtitle ? '8px' : '0'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    margin: '0',
    lineHeight: '1.3'
  };

  const subtitleStyle = {
    fontSize: '14px',
    fontWeight: '400',
    color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
    margin: '0',
    opacity: 0.8
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    flexShrink: 0
  };

  const contentStyle = {
    flex: 1,
    overflow: 'auto',
    minHeight: 0
  };

  const listStyle = {
    padding: '16px 0',
    margin: '0'
  };

  const listItemStyle = {
    padding: '12px 24px',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c20' : '#e9e8ea50'}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    transition: 'background-color 0.2s ease'
  };

  const listItemHoverStyle = {
    backgroundColor: themeMode === 'dark' ? '#302d4c20' : '#f8f9fa'
  };

  const indexStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
    minWidth: '24px',
    textAlign: 'right',
    flexShrink: 0,
    opacity: 0.7
  };

  const itemContentStyle = {
    fontSize: '14px',
    lineHeight: '1.5',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    flex: 1,
    wordBreak: 'break-word'
  };

  const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
    fontSize: '14px'
  };

  const defaultRenderItem = (item, index) => {
    // Handle different data types
    if (typeof item === 'string') {
      return <div style={itemContentStyle}>{item}</div>;
    } else if (typeof item === 'object' && item !== null) {
      // For objects, try to display a meaningful representation
      if (item.name) return <div style={itemContentStyle}>{item.name}</div>;
      if (item.title) return <div style={itemContentStyle}>{item.title}</div>;
      if (item.label) return <div style={itemContentStyle}>{item.label}</div>;
      // Fallback to JSON representation
      return <div style={itemContentStyle}>{JSON.stringify(item)}</div>;
    }
    return <div style={itemContentStyle}>{String(item)}</div>;
  };

  const handleItemMouseEnter = (e) => {
    Object.assign(e.currentTarget.style, listItemHoverStyle);
  };

  const handleItemMouseLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Sticky Header */}
          <div style={headerStyle}>
            <div style={titleRowStyle}>
              <div>
                <h2 style={titleStyle}>{title}</h2>
                {subtitle && <p style={subtitleStyle}>{subtitle}</p>}
              </div>
              <button 
                style={closeButtonStyle}
                onClick={onClose}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Scrollable Content */}
          <div style={contentStyle}>
            {data.length === 0 ? (
              <div style={emptyStateStyle}>
                No items to display
              </div>
            ) : (
              <div style={listStyle}>
                {data.map((item, index) => (
                  <div
                    key={index}
                    style={listItemStyle}
                    onMouseEnter={handleItemMouseEnter}
                    onMouseLeave={handleItemMouseLeave}
                  >
                    {showIndex && (
                      <div style={indexStyle}>
                        {index + 1}.
                      </div>
                    )}
                    {renderItem ? renderItem(item, index) : defaultRenderItem(item, index)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ListModal;
